import Section from '@/components/common/Section';
import Card from '@/components/cards/Card';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';
import { useState, useEffect, useRef } from 'react';

const industries = [
  {
    title: "OEM & Manufacturing",
    description: "Industrial automation systems, supply chain optimization, IoT integration, and production monitoring solutions.",
    icon: <img src="/icons/OEM%20&%20Manufacturing.png" alt="OEM & Manufacturing" className="w-24 h-24 object-contain" />,
    solutions: [
      "Industrial IoT platforms",
      "Production monitoring systems",
      "Supply chain optimization",
      "Quality management systems",
      "Predictive maintenance"
    ],
    caseStudy: "Automated quality control system reducing defect rates by 85%",
    detailedCaseStudy: {
      client: "Global Automotive Parts Manufacturer",
      challenge: "Manual inspection processes resulted in 12% defect rates, causing costly recalls and safety concerns across three global facilities.",
      solution: "AI-powered quality control system with computer vision, IoT sensors, and predictive analytics providing real-time monitoring.",
      implementation: "Deployed in three phases over 18 months with comprehensive staff training and seamless workflow integration.",
      results: "Defect rates reduced from 12% to 1.8% (85% improvement), $2.3M annual savings, 23% throughput increase.",
      
    }
  },
  {
    title: "Food, Dairy & Agriculture",
    description: "Farm management systems, processing optimization, traceability solutions, and supply chain management.",
    icon: <img src="/icons/Food%20&%20Agriculture.png" alt="Food & Agriculture" className="w-24 h-24 object-contain" />,
    solutions: [
      "Farm management platforms",
      "Processing automation",
      "Traceability systems",
      "Inventory management",
      "Cold chain monitoring"
    ],
    caseStudy: "End-to-end traceability system for dairy processing company",
    detailedCaseStudy: {
      client: "Premium Dairy Cooperative Network",
      challenge: "250+ farms needed complete product traceability but relied on paper-based systems, creating compliance risks.",
      solution: "Blockchain-based platform with mobile apps, RFID tracking, temperature monitoring, and consumer QR codes.",
      implementation: "Pilot with 25 farms, then full rollout across network over 14 months with comprehensive training.",
      results: "Traceability queries reduced from 3-5 days to 2 minutes, 89% less recall waste, 34% premium pricing.",
     
    }
  },
  {
    title: "Enterprises & Institutions",
    description: "Digital transformation, legacy system modernization, workflow automation, and data analytics platforms.",
    icon: <img src="/icons/Enterprises%20&%20Institutions.png" alt="Enterprises & Institutions" className="w-24 h-24 object-contain" />,
    solutions: [
      "Digital transformation",
      "Legacy modernization",
      "Workflow automation",
      "Business intelligence",
      "Document management"
    ],
    caseStudy: "Enterprise resource planning system for multinational corporation",
    detailedCaseStudy: {
      client: "Global Healthcare Institution Network",
      challenge: "15-year-old ERP across 15 countries caused delays in procurement and reporting, impacting patient care.",
      solution: "Cloud-based ERP with workflow automation, real-time analytics, and mobile access for unified operations.",
      implementation: "Phased deployment over 24 months with zero healthcare service disruption and comprehensive training.",
      results: "Transaction time reduced from 5-7 days to 24 hours, 18% procurement savings, 31% productivity increase.",
      
    }
  },
];

const technologies = [
  {
    category: "Cloud Platforms",
    items: ["Microsoft Azure", "AWS", "Google Cloud"],
    icon: "☁️"
  },
  {
    category: "Development",
    items: ["React", "Node.js", ".NET", "Python"],
    icon: "💻"
  },
  {
    category: "Data & AI",
    items: ["Machine Learning", "Power BI", "SQL Server", "MongoDB"],
    icon: "📊"
  },
  {
    category: "Integration",
    items: ["REST APIs", "GraphQL", "Microservices", "Event-Driven"],
    icon: "🔌"
  },
];

export default function IndustriesPage() {
  const [activeCaseStudy, setActiveCaseStudy] = useState<number | null>(null);
  const caseStudyRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeCaseStudy !== null) {
        const currentRef = caseStudyRefs.current[activeCaseStudy];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setActiveCaseStudy(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeCaseStudy]);

  const handleViewDetails = (index: number) => {
    setActiveCaseStudy(index);
  };
  return (
    <>
      {/* Industries Overview */}
      <Section
        title="Industries We Serve"
        subtitle="Deep domain expertise across diverse industry verticals"
        centered
        background="gradient"
      >
        <motion.p 
          className="text-lg text-textSecondary max-w-4xl mx-auto text-center"
          variants={fadeUp}
        >
          Our team combines technical excellence with deep industry knowledge to deliver 
          solutions that address real-world challenges. We understand the unique requirements, 
          regulations, and operational complexities of each industry we serve.
        </motion.p>
      </Section>

      {/* Main Industries */}
      <Section>
        <div className="space-y-12">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              ref={(el) => (caseStudyRefs.current[index] = el)}
              className="bg-surface border border-border rounded-xl p-1 md:p-2 flex flex-col"
              variants={fadeUp}
              whileHover={{ boxShadow: "0px 20px 40px rgba(0,0,0,0.15)" }}
            >
              <div className="flex flex-col">
                {activeCaseStudy === index ? (
                  // Case Study View
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center justify-center w-full min-h-[220px] py-4 px-6 relative"
                  >
                    {/* Back Button - Fixed Position Top Left */}
                    <button 
                      onClick={() => setActiveCaseStudy(null)}
                      className="absolute left-6 top-4 inline-flex items-center text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                    {/* Centered Title Section */}
                    <div className="flex items-center gap-3 justify-center mb-6">
                      <div className="text-5xl">{industry.icon}</div>
                      <div className="text-center">
                        <h3 className="text-2xl md:text-2xl font-heading font-semibold text-textPrimary">
                          {industry.title} Case Study
                        </h3>
                        <p className="text-sm font-medium text-textSecondary mt-1">
                          {industry.detailedCaseStudy.client}
                        </p>
                      </div>
                    </div>
                    {/* Centered Content List */}
                    <ul className="space-y-3 text-sm text-textSecondary max-w-4xl mx-auto" style={{listStyle: 'none', padding: 0}}>
                      <li className="text-center">
                        <span className="inline-block mr-2">•</span>
                        <strong>Challenge:</strong> {industry.detailedCaseStudy.challenge}
                      </li>
                      <li className="text-center">
                        <span className="inline-block mr-2">•</span>
                        <strong>Implementation:</strong> {industry.detailedCaseStudy.implementation}
                      </li>
                      <li className="text-center">
                        <span className="inline-block mr-2">•</span>
                        <strong>Solution:</strong> {industry.detailedCaseStudy.solution}
                      </li>
                      <li className="text-center">
                        <span className="inline-block mr-2">•</span>
                        <strong>Results:</strong> {industry.detailedCaseStudy.results}
                      </li>
                    </ul>
                  </motion.div>
                ) : (
                  // Original Industry Card
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start h-full">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start mb-6">
                        <div className="text-6xl mr-6">{industry.icon}</div>
                        <div>
                          <h3 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
                            {industry.title}
                          </h3>
                          <p className="text-lg text-textSecondary leading-relaxed">
                            {industry.description}
                          </p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-textPrimary uppercase tracking-wider mb-3">
                          Key Solutions
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {industry.solutions.map((solution, solutionIndex) => (
                            <span
                              key={solutionIndex}
                              className="text-sm px-4 py-2 bg-primary/10 text-primary rounded-full border border-primary/20"
                            >
                              {solution}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Case Study Preview */}
                    <div className="bg-background border border-border rounded-lg p-6">
                      <h4 className="text-sm font-medium uppercase tracking-wider mb-3" style={{ color: '#162660' }}>
                        Case Study
                      </h4>
                      <p className="text-textSecondary text-sm leading-relaxed line-clamp-3">
                        {industry.caseStudy}
                      </p>
                      <button 
                        onClick={() => handleViewDetails(index)}
                        className="inline-flex items-center text-primary hover:text-primary/80 font-medium text-sm mt-4 transition-colors"
                      >
                        View Details
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Technology Stack */}
      <Section
        title="Technology Expertise"
        subtitle="Modern technology stack aligned to real-world operations"
        centered
        background="surface"
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
        >
          {technologies.map((tech, index) => (
            <motion.div key={index} variants={fadeUp}>
              <Card
                title={tech.category}
                description={tech.items.join(", ")}
                icon={tech.icon}
                compact
              />
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Supporting Statement */}
      <Section centered>
        <motion.div 
          className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 md:p-12 text-center"
          variants={fadeUp}
        >
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary mb-4">
            Technology Aligned to Real-World Operations
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            We don't just build software; we create solutions that integrate seamlessly with 
            your existing operations, workflows, and business processes. Our deep industry 
            expertise ensures that every solution is practical, scalable, and delivers 
            measurable business value.
          </p>
        </motion.div>
      </Section>

      {/* Call to Action */}
      <Section centered background="surface">
        <motion.div 
          className="text-center"
          variants={fadeUp}
        >
          <h2 className="text-3xl md:text-5xl font-heading font-semibold text-textPrimary mb-6">
            Let's Discuss Your Industry Needs
          </h2>
          <p className="text-lg text-textSecondary mb-8 max-w-2xl mx-auto">
            Every industry has unique challenges and opportunities. Let's explore how our 
            expertise can help you build solutions that drive real business impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-glow transition-all duration-normal"
              onClick={() => window.open('https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2Tn6Hhto3E6w_Lc3RzjVLe9_4a7r6TyHakC1qAy8MfThNmP8I3Fxnk5rPu1jVjbbG3axqFYxiu', '_blank')}
            >
              Schedule Consultation
            </button>
            <button className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-normal">
              View Portfolio
            </button>
          </div>
        </motion.div>
      </Section>
    </>
  );
}